import { createSignal, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import {
  type EncryptedBackup,
  exportEncryptedBackup,
  exportPlaintextCsv,
  importCsv,
  importEncryptedBackup,
  type VaultItemPlain,
} from "@boveda/shared";
import { Download, Upload } from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { TextField } from "@fleet/ui";
import { useI18n } from "@/providers/i18n-provider";

/** Downloads text as file (100% on client). */
function downloadText(filename: string, text: string, mime: string): void {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.append(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Vault Import / Export. CSV import (Bitwarden/Chrome/generic) encrypting on
 * client; ENCRYPTED backup export (zero-knowledge, re-importable with
 * passphrase); PLAIN export with strong confirmation (portability).
 */
export function ImportExportPanel(props: {
  /** Plain vault items (for export). */
  items: readonly VaultItemPlain[];
  /** Persists batch of imported items (each encrypted to vault). */
  saveMany: (items: readonly VaultItemPlain[]) => Promise<number>;
}) {
  const { t } = useI18n();
  const [csvText, setCsvText] = createSignal("");
  const [status, setStatus] = createSignal("");
  const [error, setError] = createSignal("");
  const [backupPass, setBackupPass] = createSignal("");
  const [restorePass, setRestorePass] = createSignal("");
  const [restoreBlob, setRestoreBlob] = createSignal("");
  const [plainConfirm, setPlainConfirm] = createSignal("");

  function reset(): void {
    setStatus("");
    setError("");
  }

  async function handleImportCsv(): Promise<void> {
    reset();
    try {
      const { items, format, skipped } = importCsv(csvText());
      if (items.length === 0) {
        setError(t(msg`No importable rows were found.`));
        return;
      }
      const saved = await props.saveMany(items);
      setCsvText("");
      setStatus(t(msg`Imported ${saved} item(s) from ${format} CSV (${skipped} skipped).`));
    } catch {
      setError(t(msg`Could not parse the CSV.`));
    }
  }

  async function handleExportEncrypted(): Promise<void> {
    reset();
    if (backupPass().length < 8) {
      setError(t(msg`Use a passphrase of at least 8 characters.`));
      return;
    }
    try {
      setStatus(t(msg`Encrypting backup…`));
      const backup = await exportEncryptedBackup(backupPass(), props.items);
      downloadText("boveda-backup.json", JSON.stringify(backup, null, 2), "application/json");
      setBackupPass("");
      setStatus(t(msg`Encrypted backup downloaded.`));
    } catch {
      setError(t(msg`Could not create the backup.`));
    }
  }

  async function handleImportEncrypted(): Promise<void> {
    reset();
    try {
      // Trust boundary: JSON produced by this app; if corrupted, open fails.
      // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
      const backup = JSON.parse(restoreBlob()) as EncryptedBackup;
      const items = await importEncryptedBackup(restorePass(), backup);
      const saved = await props.saveMany(items);
      setRestoreBlob("");
      setRestorePass("");
      setStatus(t(msg`Restored ${saved} item(s) from the encrypted backup.`));
    } catch {
      setError(t(msg`Wrong passphrase or invalid backup file.`));
    }
  }

  function handleExportPlaintext(): void {
    reset();
    if (plainConfirm() !== "EXPORT") {
      setError(t(msg`Type EXPORT to confirm the plaintext export.`));
      return;
    }
    const csv = exportPlaintextCsv(props.items);
    downloadText("boveda-plaintext.csv", csv, "text/csv");
    setPlainConfirm("");
    setStatus(t(msg`Plaintext CSV downloaded — store it safely and delete it soon.`));
  }

  return (
    <div class="flex flex-col gap-6" data-testid="import-export-panel">
      <Show when={status()}>
        <p class="rounded-md bg-primary/10 px-3 py-2 text-sm text-primary" role="status">
          {status()}
        </p>
      </Show>
      <Show when={error()}>
        <p class="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive" role="alert">
          {error()}
        </p>
      </Show>

      {/* ── Import CSV ─────────────────────────────────────────── */}
      <section class="flex flex-col gap-2">
        <h2 class="flex items-center gap-1.5 text-sm font-semibold">
          <Upload class="h-4 w-4" /> {t(msg`Import from CSV`)}
        </h2>
        <p class="text-xs text-muted-foreground">
          {t(
            msg`Paste a CSV exported from Bitwarden, Chrome or a generic manager. Items are encrypted on this device.`,
          )}
        </p>
        <textarea
          class="min-h-24 w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-xs"
          placeholder={t(msg`name,username,password,url`)}
          value={csvText()}
          onInput={(e) => setCsvText(e.currentTarget.value)}
          data-testid="import-csv-text"
        />
        <div>
          <Button size="sm" onClick={() => void handleImportCsv()} data-testid="import-csv-submit">
            {t(msg`Import CSV`)}
          </Button>
        </div>
      </section>

      {/* ── Encrypted backup ──────────────────────────────────── */}
      <section class="flex flex-col gap-2">
        <h2 class="flex items-center gap-1.5 text-sm font-semibold">
          <Download class="h-4 w-4" /> {t(msg`Encrypted backup`)}
        </h2>
        <p class="text-xs text-muted-foreground">
          {t(
            msg`A zero-knowledge blob that only opens with this passphrase. The server never sees it.`,
          )}
        </p>
        <TextField
          type="password"
          placeholder={t(msg`Backup passphrase`)}
          value={backupPass()}
          onInput={(e) => setBackupPass(e.currentTarget.value)}
          data-testid="backup-passphrase"
        />
        <div>
          <Button
            size="sm"
            onClick={() => void handleExportEncrypted()}
            data-testid="backup-export-submit"
          >
            {t(msg`Download encrypted backup`)}
          </Button>
        </div>
      </section>

      {/* ── Restore encrypted backup ──────────────────────────── */}
      <section class="flex flex-col gap-2">
        <h2 class="text-sm font-semibold">{t(msg`Restore encrypted backup`)}</h2>
        <textarea
          class="min-h-16 w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-xs"
          placeholder={t(msg`Paste the encrypted backup JSON`)}
          value={restoreBlob()}
          onInput={(e) => setRestoreBlob(e.currentTarget.value)}
          data-testid="restore-blob"
        />
        <TextField
          type="password"
          placeholder={t(msg`Backup passphrase`)}
          value={restorePass()}
          onInput={(e) => setRestorePass(e.currentTarget.value)}
          data-testid="restore-passphrase"
        />
        <div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => void handleImportEncrypted()}
            data-testid="restore-submit"
          >
            {t(msg`Restore backup`)}
          </Button>
        </div>
      </section>

      {/* ── Plaintext export (dangerous) ──────────────────────── */}
      <section class="flex flex-col gap-2 rounded-md border border-destructive/40 p-3">
        <h2 class="text-sm font-semibold text-destructive">{t(msg`Export in plaintext`)}</h2>
        <p class="text-xs text-muted-foreground">
          {t(
            msg`Exposes every secret in readable CSV (for portability). Anyone with the file can read it. Type EXPORT to confirm.`,
          )}
        </p>
        <TextField
          placeholder="EXPORT"
          value={plainConfirm()}
          onInput={(e) => setPlainConfirm(e.currentTarget.value)}
          data-testid="plaintext-confirm"
        />
        <div>
          <Button
            size="sm"
            variant="destructive"
            onClick={() => handleExportPlaintext()}
            data-testid="plaintext-export-submit"
          >
            {t(msg`Export plaintext CSV`)}
          </Button>
        </div>
      </section>
    </div>
  );
}
